import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { MARKETPLACE_HOMEPAGE_ROUTES } from '../config/routes';
import { LoadingScreen } from 'features/common/components/LoadingScreen';
import { getBaseMarketplaceAppUrl } from '../config/env';
import { fetchLastSuccessfulLoginOrganizationId } from '@sitecore-ui/portal-singular';
import { IframeWithNavigationSync } from 'features/common/components/IframeWithNavigationSync';
import { useFetchTenantsData } from 'features/developer/core/auth/hooks/useFetchTenantsData';
import { useEffect, useMemo } from 'react';

export const MarketplaceRoutes: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const { tenantsData, isLoading: tenantsLoading } = useFetchTenantsData();

  const organizationId = fetchLastSuccessfulLoginOrganizationId();
  const marketplaceAppUrl = getBaseMarketplaceAppUrl();

  const isDetailsPath = location.pathname.includes('/details');
  const isProfilePath = location.pathname.includes('/profile');
  const isAppIdeasPath =
    location.pathname === MARKETPLACE_HOMEPAGE_ROUTES.APP_IDEAS;
  const appIdeaDetailsMatch = location.pathname.match(
    /^\/marketplace\/app-ideas\/([^/?#]+)/,
  );
  const appIdeaId = appIdeaDetailsMatch?.[1];

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const id = searchParams.get('id');
  const appId = searchParams.get('appId');
  const edit = searchParams.get('edit');
  const isEditMode = edit === 'true';

  // Clean up URL by removing query params not relevant to current subpath
  useEffect(() => {
    const shouldRemoveIdAndAppId =
      !isDetailsPath && !isProfilePath && (id || appId);
    const shouldRemoveEdit = !appIdeaId && edit !== null;
    const shouldRemovePageAndSearch =
      !isAppIdeasPath &&
      !appIdeaId &&
      (searchParams.has('page') ||
        searchParams.has('search') ||
        searchParams.has('sortBy'));

    if (
      shouldRemoveIdAndAppId ||
      shouldRemoveEdit ||
      shouldRemovePageAndSearch
    ) {
      const newSearchParams = new URLSearchParams(location.search);
      if (shouldRemoveIdAndAppId) {
        newSearchParams.delete('id');
        newSearchParams.delete('appId');
      }
      if (shouldRemoveEdit) {
        newSearchParams.delete('edit');
      }
      if (shouldRemovePageAndSearch) {
        newSearchParams.delete('page');
        newSearchParams.delete('search');
        newSearchParams.delete('sortBy');
      }

      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      history.replace(newUrl);
    }
  }, [
    isDetailsPath,
    isProfilePath,
    isAppIdeasPath,
    location.pathname,
    location.search,
    history,
    id,
    appId,
    appIdeaId,
    edit,
    searchParams,
  ]);

  const marketplaceTenant = tenantsData[0]?.id;

  const getIframeUrl = () => {
    const baseParams = `organization=${organizationId}&tenantId=${marketplaceTenant}`;

    if (isDetailsPath && id) {
      return `${marketplaceAppUrl}/marketplace/details?${baseParams}&id=${id}`;
    }
    if (isProfilePath && id) {
      return `${marketplaceAppUrl}/marketplace/profile?${baseParams}&id=${id}`;
    }
    if (isAppIdeasPath) {
      const page = searchParams.get('page');
      const search = searchParams.get('search');
      const sortBy = searchParams.get('sortBy');
      return `${marketplaceAppUrl}/marketplace/app-ideas?${baseParams}${
        page ? `&page=${page}` : ''
      }${search ? `&search=${encodeURIComponent(search)}` : ''}${
        sortBy ? `&sortBy=${encodeURIComponent(sortBy)}` : ''
      }`;
    }
    if (appIdeaId) {
      const page = searchParams.get('page');
      const search = searchParams.get('search');
      const sortBy = searchParams.get('sortBy');
      return `${marketplaceAppUrl}/marketplace/app-ideas/${appIdeaId}?${baseParams}${
        page ? `&page=${page}` : ''
      }${search ? `&search=${encodeURIComponent(search)}` : ''}${
        isEditMode ? '&edit=true' : ''
      }${sortBy ? `&sortBy=${encodeURIComponent(sortBy)}` : ''}`;
    }
    return `${marketplaceAppUrl}/marketplace?${baseParams}`;
  };

  if (tenantsLoading) {
    return <LoadingScreen data-testid='loading-component' message='Loading…' />;
  }

  return (
    <Route path={MARKETPLACE_HOMEPAGE_ROUTES.ROOT}>
      <Switch>
        <Route
          path={[
            MARKETPLACE_HOMEPAGE_ROUTES.ROOT,
            MARKETPLACE_HOMEPAGE_ROUTES.DETAILS,
            MARKETPLACE_HOMEPAGE_ROUTES.PROFILE,
            MARKETPLACE_HOMEPAGE_ROUTES.APP_IDEAS,
            MARKETPLACE_HOMEPAGE_ROUTES.APP_IDEA_DETAILS,
          ]}
          render={() => (
            <IframeWithNavigationSync
              src={getIframeUrl()}
              title='Marketplace Iframe'
              allow='clipboard-read; clipboard-write'
              debugMode={process.env.NODE_ENV === 'development'}
            />
          )}
        />
      </Switch>
    </Route>
  );
};
